/**
 * WabaRelinkDiagnosticPanel — P0-6 POST-APPLY · Diagnostica READ-ONLY
 * del retroactive relink WhatsApp ↔ CRM + APPLY protetto Super Admin.
 *
 * Endpoint utilizzati:
 *   GET  /api/admin/messaging/whatsapp/contacts-crm-audit       (READ)
 *   POST /api/admin/messaging/whatsapp/relink-contacts {dry_run:true}  (READ)
 *   GET  /api/admin/customers?q=musta&limit=10                  (READ)
 *   GET  /api/admin/messaging/whatsapp/contact-crm-probe        (READ)
 *   POST /api/admin/messaging/whatsapp/relink-contacts {dry_run:false, confirm_token}
 *        (WRITE su whatsapp_contacts.customer_id, solo se stato immutato).
 *
 * L'APPLY è protetto da:
 *   1. Ruolo super_admin
 *   2. Precondizioni: ambiguous=0, token audit=dry-run, would_link>0
 *   3. Conferma esplicita a 2 step con conteggi
 *   4. apply_token deterministico bound-to-state: se cambia stato fra
 *      dry-run e apply, backend risponde 409 STATE_CHANGED e non scrive
 *      nulla. La UI in tal caso obbliga a rieseguire audit + dry-run.
 *   5. Dopo APPLY OK, ri-esegue automaticamente l'audit READ-ONLY per
 *      verificare lo stato finale.
 */
import React, { useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function maskLast4(phone) {
  const digits = String(phone || "").replace(/\D+/g, "");
  return digits.length >= 4 ? "***" + digits.slice(-4) : "***";
}

function Row({ label, value, testid, tone }) {
  const bg =
    tone === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
    tone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" :
    tone === "danger" ? "bg-rose-50 border-rose-200 text-rose-900" :
    "bg-sand-100 border-sand-300 text-ink-900";
  return (
    <div className={`flex items-baseline justify-between gap-4 rounded-xl border px-3 py-2 ${bg}`}
         data-testid={testid}>
      <span className="text-sm">{label}</span>
      <span className="font-mono text-sm tabular-nums text-right break-all">{value}</span>
    </div>
  );
}

export function WabaRelinkDiagnosticPanel() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {audit, dryRun, mustaMatch, anomalies}
  const [error, setError] = useState(null);

  // APPLY state (2-step con conferma esplicita)
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);   // audit-like payload
  const [applyStateChanged, setApplyStateChanged] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    // Reset stato APPLY: ogni nuovo audit invalida qualsiasi conferma
    // precedente e forza il ricalcolo del piano.
    setApplyConfirmOpen(false);
    setApplyResult(null);
    setApplyStateChanged(false);
    setApplyError(null);
    try {
      const [auditRes, dryRunRes, mustaSearchRes] = await Promise.all([
        api.get("/admin/messaging/whatsapp/contacts-crm-audit?sample_size=100"),
        api.post("/admin/messaging/whatsapp/relink-contacts", { dry_run: true }),
        api.get("/admin/customers?q=musta&limit=10"),
      ]);
      const audit = auditRes.data || {};
      const dryRun = dryRunRes.data || {};
      const mustaCustomers = Array.isArray(mustaSearchRes.data)
        ? mustaSearchRes.data : (mustaSearchRes.data?.rows || []);

      // Diagnostica Musta CONCLUSIVA · per ogni customer trovato in CRM
      // chiama l'endpoint di probe telefono-specifico: risposta
      // deterministica indipendente dai sample limits del dry-run.
      const mustaDiag = await Promise.all(mustaCustomers.map(async (c) => {
        const cid = String(c.id || c._id || "");
        const phone = c.phone || "";
        const name = [c.name, c.surname].filter(Boolean).join(" ") || "(senza nome)";
        const masked = maskLast4(phone);
        if (!phone) {
          return {
            customer_id: cid, name, phone_masked: masked,
            would_link: false, reason: "customer_without_phone",
            phone_variant: null, contact_id: null,
          };
        }
        try {
          const probeRes = await api.get(
            `/admin/messaging/whatsapp/contact-crm-probe?phone=${encodeURIComponent(phone)}`);
          const p = probeRes.data || {};
          // H1/H2/H3 discriminanti dai nuovi campi del probe
          const allContacts = p.all_contacts_for_wa_id || [];
          const convs = p.conversations_for_wa_id || [];
          const linkedLookup = p.linked_customer_lookup || null;
          const convContactIds = new Set(
            convs.filter((cv) => cv.contact_id).map((cv) => cv.contact_id));
          const contactsCount = allContacts.length;
          const convsContactMissing = convs.some(
            (cv) => !cv.contact_id
                     || !allContacts.some((ac) => ac.contact_id === cv.contact_id));
          const convContactUnmatched = convs.some((cv) => {
            const ac = allContacts.find((a) => a.contact_id === cv.contact_id);
            return ac && !ac.customer_id;
          });
          const anyLinked = allContacts.some((ac) => ac.customer_id);
          // H1: >1 contact per stesso wa_id, la conv è legata a uno
          // unmatched mentre un altro contact è linkato.
          const h1 = contactsCount > 1 && convContactUnmatched && anyLinked;
          // H2: contact linkato ma customer inesistente o con name vuoti.
          const h2 = !!(linkedLookup
                          && (linkedLookup.exists === false
                                || linkedLookup.name_is_empty));
          // H3: nessuna conv trova il proprio contact_id fra whatsapp_contacts.
          const h3 = convs.length > 0 && convsContactMissing;
          return {
            customer_id: cid,
            name,
            phone_masked: masked,
            would_link: !!p.verdict?.would_link_exact,
            reason: p.verdict?.reason || null,
            phone_variant: p.verdict?.phone_variant || null,
            contact_id: p.wa_contact?.contact_id || null,
            wa_contact_found: !!p.wa_contact?.found,
            customer_id_on_contact: p.wa_contact?.customer_id || null,
            crm_resolution: p.crm_lookup?.resolution || null,
            crm_candidate_count: p.crm_lookup?.candidate_count ?? null,
            // Discriminante finale
            contacts_count: contactsCount,
            conversations_count: convs.length,
            all_contacts: allContacts,
            conversations: convs,
            linked_customer_lookup: linkedLookup,
            h1_dual_contact: h1,
            h2_empty_or_missing_customer: h2,
            h3_orphan_conv: h3,
            // Simulazione list_conversations (isola backend vs frontend)
            api_simulated_item: p.api_simulated_item || null,
            debug_types: p.debug_types || {},
          };
        } catch (probeErr) {
          return {
            customer_id: cid, name, phone_masked: masked,
            would_link: false, reason: "probe_error",
            phone_variant: null, contact_id: null,
          };
        }
      }));

      // Anomalie sintetiche
      const anomalies = [];
      if (audit.apply_token !== dryRun.apply_token) {
        anomalies.push({
          level: "danger",
          msg: `apply_token disallineato fra audit (${audit.apply_token}) e dry-run (${dryRun.apply_token}). Riprova: qualcuno ha modificato dati fra le due call.`,
        });
      }
      const auditProbeWouldLink = audit?.probe?.would_link_exact ?? null;
      const dryRunWouldLink = (dryRun.matched_exact || 0) + (dryRun.matched_it_legacy || 0);
      if (auditProbeWouldLink !== null && auditProbeWouldLink !== dryRunWouldLink) {
        anomalies.push({
          level: "warn",
          msg: `would_link_exact diverge: audit=${auditProbeWouldLink} vs dry-run=${dryRunWouldLink}.`,
        });
      }
      if ((audit?.probe?.ambiguous_multiple_customers || 0) > 0) {
        anomalies.push({
          level: "warn",
          msg: `Presenti ${audit.probe.ambiguous_multiple_customers} contact ambiguous: richiedono review manuale (non verranno auto-linkati).`,
        });
      }
      if (mustaCustomers.length === 0) {
        anomalies.push({
          level: "warn",
          msg: "Nessun customer con nome/cognome contenente 'musta' trovato nel CRM.",
        });
      }
      if (mustaCustomers.length > 0 &&
          !mustaDiag.some((m) => m.would_link)) {
        const firstReason = mustaDiag[0]?.reason || "unknown";
        anomalies.push({
          level: "warn",
          msg: `Nessun customer Musta risulta would_link_exact. Motivo primo record: ${firstReason}.`,
        });
      }
      const errors = dryRun.errors || [];
      if (errors.length > 0) {
        anomalies.push({
          level: "danger",
          msg: `Dry-run ha riportato ${errors.length} errori interni (vedi console).`,
        });
        // eslint-disable-next-line no-console
        console.warn("[WabaRelink] dry-run errors:", errors);
      }

      setResult({ audit, dryRun, mustaDiag, anomalies });
    } catch (e) {
      setError(formatApiError(e) || "Errore durante l'esecuzione.");
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------
  // APPLY snapshot · derivazione STRICT (no fallback silenzioso a 0)
  // ---------------------------------------------------------------------
  // Requisito utente: se un campo dello snapshot audit+dry-run manca o
  // è di tipo inatteso, APPLY DEVE essere disabilitato con errore chiaro,
  // NON silenziato con `|| 0`. Inoltre l'`audit.probe.would_link_exact`
  // deve coincidere con `dryRun.matched_exact + dryRun.matched_it_legacy`;
  // se divergono, il piano è incoerente e non si applica.
  const auWouldLinkRaw = result?.audit?.probe?.would_link_exact;
  const auAmbRaw       = result?.audit?.probe?.ambiguous_multiple_customers;
  const auNoMatchRaw   = result?.audit?.probe?.no_match;
  const auTokenRaw     = result?.audit?.apply_token;
  const drTokenRaw     = result?.dryRun?.apply_token;
  const drMatchedExact = result?.dryRun?.matched_exact;
  const drMatchedItLegacy = result?.dryRun?.matched_it_legacy;
  const drAmbiguous    = result?.dryRun?.ambiguous_multiple;
  const drNoMatch      = result?.dryRun?.no_match;

  const _isNum = (v) => typeof v === "number" && Number.isFinite(v);
  const _isStr = (v) => typeof v === "string" && v.length > 0;

  const drWouldLinkComputed = (_isNum(drMatchedExact) ? drMatchedExact : NaN)
                              + (_isNum(drMatchedItLegacy) ? drMatchedItLegacy : NaN);

  // snapshotIncomplete: campi mancanti / tipi errati / audit vs dry-run divergono
  const snapshotMissingFields = !(
    _isNum(auWouldLinkRaw) && _isNum(auAmbRaw) && _isNum(auNoMatchRaw) &&
    _isStr(auTokenRaw) && _isStr(drTokenRaw) &&
    _isNum(drMatchedExact) && _isNum(drMatchedItLegacy) &&
    _isNum(drAmbiguous) && _isNum(drNoMatch)
  );
  const snapshotTokensMismatch = _isStr(auTokenRaw) && _isStr(drTokenRaw)
                                    && auTokenRaw !== drTokenRaw;
  const snapshotCountsMismatch = (
    _isNum(auWouldLinkRaw) && _isNum(drWouldLinkComputed)
    && auWouldLinkRaw !== drWouldLinkComputed
  ) || (
    _isNum(auAmbRaw) && _isNum(drAmbiguous) && auAmbRaw !== drAmbiguous
  );
  const snapshotIncomplete = snapshotMissingFields
                              || snapshotTokensMismatch
                              || snapshotCountsMismatch;

  // Sorgente autoritativa unica per la sezione APPLY: audit.probe (stessi
  // campi mostrati sopra nei Row). Se snapshotIncomplete, i valori sono
  // null (non 0), l'UI mostra placeholder e blocca l'APPLY.
  const wouldLinkCount = snapshotIncomplete ? null : auWouldLinkRaw;
  const ambiguousCount = snapshotIncomplete ? null : auAmbRaw;
  const noMatchCount   = snapshotIncomplete ? null : auNoMatchRaw;
  const currentApplyToken = snapshotIncomplete ? "" : auTokenRaw;

  const applyEligible = !!(
    result &&
    !snapshotIncomplete &&
    auWouldLinkRaw > 0 &&
    auAmbRaw === 0
  );

  const applyDisabledReason = !result
    ? ""
    : snapshotMissingFields
    ? "Snapshot audit+dry-run incompleto (campi mancanti o tipi inattesi). Rieseguire audit + dry-run."
    : snapshotTokensMismatch
    ? "Token audit ≠ token dry-run: rieseguire audit + dry-run."
    : snapshotCountsMismatch
    ? "Conteggi audit e dry-run divergono (piano incoerente). Rieseguire audit + dry-run."
    : auAmbRaw > 0
    ? `${auAmbRaw} contatti ambiguous: risolverli manualmente prima dell'APPLY.`
    : auWouldLinkRaw === 0
    ? "Nessun contact da collegare (would_link_exact = 0)."
    : "";

  const executeApply = useCallback(async () => {
    if (!applyEligible || !currentApplyToken) return;
    setApplyLoading(true);
    setApplyStateChanged(false);
    setApplyError(null);
    setApplyResult(null);
    try {
      const resp = await api.post(
        "/admin/messaging/whatsapp/relink-contacts",
        { dry_run: false, confirm_token: currentApplyToken });
      setApplyResult(resp.data || {});
      setApplyConfirmOpen(false);
      // Ri-esegui audit READ-ONLY per mostrare stato finale
      // (non blocca la visualizzazione del risultato apply).
      setTimeout(() => { run(); }, 250);
    } catch (e) {
      // 409 STATE_CHANGED · piano mutato fra dry-run e apply
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && typeof detail === "object"
          && detail?.code === "STATE_CHANGED") {
        setApplyStateChanged(true);
        setApplyError(
          `Stato cambiato dal dry-run (nuovo token: ${detail.current_apply_token || "?"}). ` +
          `Nessuna scrittura effettuata. Ri-esegui audit + dry-run.`);
      } else {
        setApplyError(formatApiError(e) || "Errore durante l'APPLY.");
      }
      setApplyConfirmOpen(false);
    } finally {
      setApplyLoading(false);
    }
  }, [applyEligible, currentApplyToken, run]);

  if (!isSuper) return null;

  return (
    <section
      className="card-elevated p-5 rounded-2xl border border-bronze-400 bg-white"
      data-testid="waba-relink-diagnostic-panel"
    >
      <header>
        <div className="eyebrow">P0-6 · Post-Apply</div>
        <h2 className="font-serif text-2xl mt-1">
          Diagnostica relink WhatsApp ↔ CRM
        </h2>
        <p className="text-xs text-ink-500 mt-1 max-w-2xl">
          Esegue audit + dry-run del retroactive linking (contact WhatsApp
          senza <code>customer_id</code> → customer CRM via strict E.164).
          L'APPLY reale è disponibile solo dopo audit valido, con conferma
          esplicita e apply_token bound-to-state.
        </p>
      </header>

      {/* Pulsante SU RIGA DEDICATA · impossibile da nascondere da flex-wrap
          e usa solo palette bronze definita (400/500/600/700). */}
      <div className="mt-4">
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="waba-relink-diagnostic-run-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                     text-sm font-semibold bg-bronze-600 text-white
                     hover:bg-bronze-700 disabled:opacity-60
                     disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {loading ? "Esecuzione…" : "Esegui audit + dry-run"}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
             data-testid="waba-relink-diagnostic-error">
          {String(error)}
        </div>
      )}

      {result && (
        <div className="mt-4 grid gap-3 md:grid-cols-2"
             data-testid="waba-relink-diagnostic-result">
          <Row label="Totale whatsapp_contacts"
               value={result.audit?.totals?.contacts_total ?? "—"}
               testid="waba-relink-total"/>
          <Row label="Contatti senza customer_id"
               value={result.audit?.totals?.contacts_without_customer_id ?? "—"}
               testid="waba-relink-without-customer"/>
          <Row label="Would link (exact E.164)"
               value={result.audit?.probe?.would_link_exact ?? "—"}
               tone={(result.audit?.probe?.would_link_exact || 0) > 0 ? "ok" : undefined}
               testid="waba-relink-would-link"/>
          <Row label="Ambiguous (skip auto)"
               value={result.audit?.probe?.ambiguous_multiple_customers ?? "—"}
               tone={(result.audit?.probe?.ambiguous_multiple_customers || 0) > 0 ? "warn" : undefined}
               testid="waba-relink-ambiguous"/>
          <Row label="No match"
               value={result.audit?.probe?.no_match ?? "—"}
               testid="waba-relink-no-match"/>
          <Row label="apply_token"
               value={result.audit?.apply_token ?? "—"}
               testid="waba-relink-apply-token"/>
          <Row label="Token audit = token dry-run"
               value={
                 result.audit?.apply_token && result.dryRun?.apply_token
                   ? (result.audit.apply_token === result.dryRun.apply_token ? "SÌ" : "NO")
                   : "—"
               }
               tone={
                 result.audit?.apply_token === result.dryRun?.apply_token ? "ok" : "danger"
               }
               testid="waba-relink-token-match"/>

          {/* Musta diagnostic block */}
          <div className="md:col-span-2 mt-2 rounded-xl border border-bronze-400
                          bg-sand-100 p-3"
               data-testid="waba-relink-musta">
            <div className="text-sm font-semibold text-bronze-700 mb-2">
              Verifica specifica · cliente Musta · Test A + Test B
            </div>
            {result.mustaDiag.length === 0 && (
              <div className="text-xs text-ink-500">
                Nessun customer con nome/cognome contenente "musta" trovato nel CRM.
              </div>
            )}
            {result.mustaDiag.map((m) => (
              <div key={m.customer_id} className="mb-3 pb-3 border-b border-sand-300 last:border-b-0 last:mb-0 last:pb-0">
                <div className="flex items-baseline justify-between gap-3 py-1 text-xs">
                  <span className="text-ink-700 font-semibold">
                    {m.name} · <span className="font-mono">{m.phone_masked}</span> · CRM_id ***{m.customer_id.slice(-6)}
                  </span>
                  <span className={m.would_link ? "text-emerald-800 font-semibold" : "text-rose-800 font-medium"}>
                    {m.would_link ? "WOULD LINK: YES" : `WOULD LINK: NO · ${m.reason || "unknown"}`}
                  </span>
                </div>

                {/* TEST A · dump contacts per wa_id */}
                <div className="mt-2 text-[11px]">
                  <div className="font-semibold text-ink-900 mb-1">
                    Test A · whatsapp_contacts trovati con wa_id ({m.contacts_count || 0})
                  </div>
                  {(m.all_contacts || []).length === 0 && (
                    <div className="text-ink-500 pl-2">Nessun contact.</div>
                  )}
                  {(m.all_contacts || []).map((ac) => (
                    <div key={ac.contact_id}
                         className="pl-2 py-0.5 flex items-baseline gap-3 flex-wrap font-mono">
                      <span>_id ***{ac.contact_id.slice(-6)}</span>
                      <span>pnid ***{String(ac.phone_number_id || "").slice(-6) || "—"}</span>
                      <span>customer_id {ac.customer_id ? `***${ac.customer_id.slice(-6)}` : "NULL"}</span>
                      <span className={ac.is_conversation_contact
                        ? "text-emerald-800 font-semibold"
                        : "text-ink-500"}>
                        conv_ref: {ac.is_conversation_contact ? "SÌ" : "NO"}
                      </span>
                      <span className="text-ink-400">
                        [{ac.customer_match_status || "unmatched"}]
                      </span>
                    </div>
                  ))}
                  {(m.conversations || []).length > 0 && (
                    <div className="pl-2 mt-1 text-ink-600">
                      conv attive per wa_id: {m.conversations.length} · contact_id refs: {
                        m.conversations.map((cv) => cv.contact_id
                          ? `***${cv.contact_id.slice(-6)}` : "NULL").join(", ")
                      }
                    </div>
                  )}
                </div>

                {/* TEST B · linked customer exists */}
                <div className="mt-2 text-[11px]">
                  <div className="font-semibold text-ink-900 mb-1">
                    Test B · customer linkato al contact probe esiste?
                  </div>
                  {m.linked_customer_lookup ? (
                    <div className="pl-2 font-mono">
                      exists:{" "}
                      <span className={m.linked_customer_lookup.exists
                        ? "text-emerald-800" : "text-rose-800 font-semibold"}>
                        {m.linked_customer_lookup.exists ? "YES" : "NO"}
                      </span>
                      {m.linked_customer_lookup.exists && (
                        <> · name="{m.linked_customer_lookup.name || ""}" · surname="{m.linked_customer_lookup.surname || ""}"
                          {m.linked_customer_lookup.name_is_empty && (
                            <span className="text-rose-800 font-semibold"> · NAME_EMPTY!</span>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="pl-2 text-ink-500">
                      Contact non ha customer_id (nessun lookup necessario).
                    </div>
                  )}
                </div>

                {/* Verdetto H1/H2/H3 per questa riga */}
                <div className="mt-2 text-[11px] flex gap-3 flex-wrap">
                  <span className={m.h1_dual_contact
                    ? "text-rose-800 font-semibold" : "text-ink-500"}>
                    H1 dual_contact: {m.h1_dual_contact ? "CONFIRMED" : "EXCLUDED"}
                  </span>
                  <span className={m.h2_empty_or_missing_customer
                    ? "text-rose-800 font-semibold" : "text-ink-500"}>
                    H2 empty_or_missing_customer: {m.h2_empty_or_missing_customer ? "CONFIRMED" : "EXCLUDED"}
                  </span>
                  <span className={m.h3_orphan_conv
                    ? "text-rose-800 font-semibold" : "text-ink-500"}>
                    H3 orphan_conv: {m.h3_orphan_conv ? "CONFIRMED" : "EXCLUDED"}
                  </span>
                </div>

                {/* Simulazione list_conversations · verdetto backend/serializ/frontend */}
                {m.api_simulated_item && (
                  <div className="mt-2 text-[11px] rounded border border-sand-300 bg-white p-2">
                    <div className="font-semibold text-ink-900 mb-1">
                      Simulazione list_conversations (stessa logica reale)
                    </div>
                    <div className="font-mono text-[10px] space-y-0.5">
                      <div>contact_id_raw_from_conv: <span className="text-ink-700">{m.api_simulated_item.contact_id_raw_from_conv?.slice(-8) || "NULL"}</span> · pytype: <span className={m.debug_types?.conv_contact_id_pytype === "ObjectId" ? "text-emerald-700" : "text-rose-800 font-semibold"}>{m.debug_types?.conv_contact_id_pytype || "?"}</span></div>
                      <div>contact_hit_found_by_query ($in): <span className={m.api_simulated_item.contact_hit_found_by_query ? "text-emerald-700" : "text-rose-800 font-semibold"}>{m.api_simulated_item.contact_hit_found_by_query ? "YES" : "NO"}</span></div>
                      <div>contact.customer_id pytype: <span className={m.debug_types?.contact_customer_id_pytype === "ObjectId" ? "text-emerald-700" : m.debug_types?.contact_customer_id_pytype === "NoneType" ? "text-rose-800 font-semibold" : "text-amber-800 font-semibold"}>{m.debug_types?.contact_customer_id_pytype || "?"}</span></div>
                      <div>customer_hit_found_by_query ($in): <span className={m.api_simulated_item.customer_hit_found_by_query ? "text-emerald-700" : "text-rose-800 font-semibold"}>{m.api_simulated_item.customer_hit_found_by_query ? "YES" : "NO"}</span></div>
                      <div className="border-t border-sand-300 mt-1 pt-1">
                        <span className="font-semibold">API customer_id: </span>
                        <span className={m.api_simulated_item.customer_id_output ? "text-emerald-700" : "text-rose-800 font-semibold"}>
                          {m.api_simulated_item.customer_id_output ? `***${m.api_simulated_item.customer_id_output.slice(-6)}` : "NULL"}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold">API customer_name: </span>
                        <span className={m.api_simulated_item.customer_name_output ? "text-emerald-700" : "text-rose-800 font-semibold"}>
                          {m.api_simulated_item.customer_name_output ? `"${m.api_simulated_item.customer_name_output}"` : "NULL"}
                        </span>
                      </div>
                      <div className="text-ink-500">
                        DISPLAY BRANCH:{" "}
                        <span className="font-semibold">
                          {m.api_simulated_item.customer_name_output
                            ? "CRM_NAME"
                            : "PHONE_FALLBACK"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Anomalies */}
          <div className="md:col-span-2 mt-2"
               data-testid="waba-relink-anomalies">
            <div className="text-sm font-semibold text-ink-900 mb-2">
              Anomalie rilevate
            </div>
            {result.anomalies.length === 0 && (
              <div className="text-xs text-emerald-800 rounded-lg border
                              border-emerald-200 bg-emerald-50 px-3 py-2">
                Nessuna anomalia.
              </div>
            )}
            {result.anomalies.map((a, i) => (
              <div key={i}
                   className={`text-xs rounded-lg border px-3 py-2 mb-2 ${
                     a.level === "danger"
                       ? "border-rose-200 bg-rose-50 text-rose-900"
                       : "border-amber-200 bg-amber-50 text-amber-900"
                   }`}>
                {a.msg}
              </div>
            ))}
          </div>

          <div className="md:col-span-2 mt-2 text-[11px] text-ink-500 leading-relaxed">
            Match strict E.164 su <code>customers.phone</code> · fallback
            esclusivo IT-legacy (+39 → variante locale) · nessun fuzzy · nessun
            suffix cross-country · AMBIGUOUS mai auto-linkati.
            <span className="ml-2">
              apply_token è vincolato allo stato corrente: se cambia anche un
              solo contact/customer, viene rigenerato al prossimo run.
            </span>
          </div>

          {/* ================================================================
              APPLY SECTION · protetta Super Admin · 2-step confirmation
              Solo abilitata se ambiguous=0 AND token audit=dry-run AND would_link>0.
              Backend rifiuta con 409 STATE_CHANGED se stato mutato (nessuna scrittura).
              ================================================================ */}
          <div className="md:col-span-2 mt-4 rounded-2xl border-2 border-bronze-400 bg-white p-4"
               data-testid="waba-relink-apply-section">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <div className="eyebrow text-bronze-700">Apply · Super Admin</div>
                <h3 className="font-serif text-xl mt-1 text-ink-900"
                    data-testid="waba-relink-apply-title">
                  {wouldLinkCount === null
                    ? "Applica relink · snapshot incompleto"
                    : `Applica relink ${wouldLinkCount} contatt${wouldLinkCount === 1 ? "o" : "i"}`}
                </h3>
                <p className="text-xs text-ink-500 mt-1">
                  Scrittura consentita esclusivamente su <code>whatsapp_contacts.customer_id</code>
                  {" "}ancora <code>NULL</code> secondo il piano validato. Nessun customer nuovo,
                  nessuna modifica a conversazioni/messaggi/prenotazioni, nessuna call Meta/WABA.
                </p>
                {/* Snapshot raw values esposti per audit/debug */}
                <div className="mt-2 text-[10px] font-mono text-ink-500"
                     data-testid="waba-relink-apply-snapshot-raw">
                  snapshot · audit.probe.would_link_exact=<span className={_isNum(auWouldLinkRaw) ? "text-emerald-700" : "text-rose-800 font-semibold"}>{_isNum(auWouldLinkRaw) ? auWouldLinkRaw : String(auWouldLinkRaw)}</span>
                  {" · "}audit.probe.ambiguous_multiple_customers=<span className={_isNum(auAmbRaw) ? "text-emerald-700" : "text-rose-800 font-semibold"}>{_isNum(auAmbRaw) ? auAmbRaw : String(auAmbRaw)}</span>
                  {" · "}audit.probe.no_match=<span className={_isNum(auNoMatchRaw) ? "text-emerald-700" : "text-rose-800 font-semibold"}>{_isNum(auNoMatchRaw) ? auNoMatchRaw : String(auNoMatchRaw)}</span>
                  {" · "}dryRun.matched_exact+it_legacy=<span className={_isNum(drWouldLinkComputed) ? "text-emerald-700" : "text-rose-800 font-semibold"}>{_isNum(drWouldLinkComputed) ? drWouldLinkComputed : "?"}</span>
                  {" · "}audit.apply_token={auTokenRaw || "MISSING"}
                  {" · "}dryRun.apply_token={drTokenRaw || "MISSING"}
                </div>
              </div>
              {!applyResult && !applyStateChanged && (
                <button
                  type="button"
                  disabled={!applyEligible || applyLoading}
                  onClick={() => setApplyConfirmOpen(true)}
                  data-testid="waba-relink-apply-btn"
                  className="px-5 py-2.5 rounded-full text-sm font-semibold
                             bg-bronze-600 text-white hover:bg-bronze-700
                             disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors shadow-sm"
                  title={!applyEligible ? applyDisabledReason : ""}
                >
                  {wouldLinkCount === null
                    ? "Snapshot incompleto"
                    : `Applica relink ${wouldLinkCount} contatt${wouldLinkCount === 1 ? "o" : "i"}`}
                </button>
              )}
            </div>

            {/* Motivo disabilitazione esplicito */}
            {!applyEligible && !applyResult && !applyStateChanged && applyDisabledReason && (
              <div className="mt-2 text-[11px] text-amber-800 rounded-lg border
                              border-amber-200 bg-amber-50 px-3 py-2"
                   data-testid="waba-relink-apply-disabled-reason">
                {applyDisabledReason}
              </div>
            )}

            {/* Errore APPLY generico */}
            {applyError && !applyStateChanged && (
              <div className="mt-3 text-xs rounded-lg border border-rose-200
                              bg-rose-50 px-3 py-2 text-rose-900"
                   data-testid="waba-relink-apply-error">
                {applyError}
              </div>
            )}

            {/* 409 STATE_CHANGED · obbliga a rieseguire audit */}
            {applyStateChanged && (
              <div className="mt-3 rounded-lg border-2 border-rose-300
                              bg-rose-50 p-3"
                   data-testid="waba-relink-apply-state-changed">
                <div className="text-sm font-semibold text-rose-900 mb-1">
                  Stato cambiato · nessuna scrittura effettuata
                </div>
                <div className="text-xs text-rose-800 mb-3">
                  {applyError}
                </div>
                <button
                  type="button"
                  onClick={run}
                  disabled={loading}
                  data-testid="waba-relink-apply-retry-audit"
                  className="px-4 py-2 rounded-full text-xs font-semibold
                             bg-rose-700 text-white hover:bg-rose-800
                             disabled:opacity-50">
                  Riesegui audit + dry-run
                </button>
              </div>
            )}

            {/* Risultato APPLY */}
            {applyResult && (
              <div className="mt-3 rounded-lg border-2 border-emerald-300
                              bg-emerald-50 p-3"
                   data-testid="waba-relink-apply-result">
                <div className="text-sm font-semibold text-emerald-900 mb-2">
                  APPLY eseguito · {applyResult.updated || 0} contatt{applyResult.updated === 1 ? "o" : "i"} collegat{applyResult.updated === 1 ? "o" : "i"}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="rounded border border-emerald-200 bg-white px-2 py-1">
                    <div className="text-ink-500 text-[10px]">linked (updated)</div>
                    <div className="font-mono font-semibold text-emerald-800">{applyResult.updated || 0}</div>
                  </div>
                  <div className="rounded border border-sand-300 bg-white px-2 py-1">
                    <div className="text-ink-500 text-[10px]">skipped</div>
                    <div className="font-mono">{applyResult.skipped_max_updates || 0}</div>
                  </div>
                  <div className="rounded border border-sand-300 bg-white px-2 py-1">
                    <div className="text-ink-500 text-[10px]">ambiguous (skip)</div>
                    <div className="font-mono">{applyResult.ambiguous_multiple || 0}</div>
                  </div>
                  <div className="rounded border border-sand-300 bg-white px-2 py-1">
                    <div className="text-ink-500 text-[10px]">errors</div>
                    <div className={`font-mono ${(applyResult.errors || []).length > 0 ? "text-rose-800 font-semibold" : ""}`}>
                      {(applyResult.errors || []).length}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-[10px] text-ink-500 font-mono">
                  apply_token: {applyResult.apply_token || "?"} · scanned: {applyResult.scanned || 0} · no_match: {applyResult.no_match || 0}
                </div>
                <div className="mt-2 text-[11px] text-emerald-800">
                  Audit READ-ONLY ri-eseguito automaticamente: i contatori sopra riflettono lo stato aggiornato.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL · conferma esplicita APPLY */}
      {applyConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm p-4"
             data-testid="waba-relink-apply-confirm-overlay"
             onClick={() => !applyLoading && setApplyConfirmOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
               onClick={(e) => e.stopPropagation()}
               data-testid="waba-relink-apply-confirm-modal">
            <h4 className="font-serif text-xl text-ink-900 mb-2">
              Conferma relink retroattivo
            </h4>
            <p className="text-sm text-ink-700 mb-3">
              Stai per collegare <span className="font-semibold">{wouldLinkCount ?? "?"}</span> contatt{wouldLinkCount === 1 ? "o WhatsApp" : "i WhatsApp"} ai relativi customer CRM via strict E.164.
            </p>
            <ul className="text-xs text-ink-700 space-y-1 mb-4
                            rounded-lg border border-sand-300 bg-sand-100 p-3">
              <li><span className="font-mono font-semibold text-emerald-800">{wouldLinkCount ?? "?"}</span> da collegare (would_link_exact)</li>
              <li><span className="font-mono font-semibold text-amber-800">{ambiguousCount ?? "?"}</span> ambigui (non modificati)</li>
              <li><span className="font-mono">{noMatchCount ?? "?"}</span> no-match (ignorati)</li>
            </ul>
            <div className="text-[11px] text-ink-500 mb-4 leading-relaxed">
              Scrittura solo su <code>whatsapp_contacts.customer_id</code> con guard <code>NULL</code>.
              Se lo stato è cambiato dal dry-run, l'operazione verrà rifiutata senza scrivere.
              <div className="mt-1 font-mono">apply_token: {currentApplyToken}</div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={applyLoading}
                onClick={() => setApplyConfirmOpen(false)}
                data-testid="waba-relink-apply-cancel"
                className="px-4 py-2 rounded-full text-sm border-2 border-sand-300
                           hover:bg-sand-100 text-ink-700
                           disabled:opacity-40">
                Annulla
              </button>
              <button
                type="button"
                disabled={applyLoading}
                onClick={executeApply}
                data-testid="waba-relink-apply-confirm"
                className="px-5 py-2 rounded-full text-sm font-semibold
                           bg-bronze-600 text-white hover:bg-bronze-700
                           disabled:opacity-40 inline-flex items-center gap-2">
                {applyLoading ? "Applicando…" : `Conferma · applica ${wouldLinkCount ?? "?"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default WabaRelinkDiagnosticPanel;
